/**
 * Na een handeling alleen bijwerken wat geraakt is (sessie 72, blok 1).
 *
 * Tot sessie 72 herlaadde elke handeling alles onder `["v6"]`: lijst,
 * zaak, concept, controles, copiloot, klant en orders (8 tot 10 aanvragen,
 * gemeten in `docs/acceptatie/sessie-72/snelheid-voor.md`). Nu:
 *
 * - de kaart gaat in de lijstcache meteen van Open naar zijn nieuwe map,
 *   met de tellers erbij (`verplaatsKaart`);
 * - de zaak zelf krijgt zijn nieuwe stand in de cache (`zetZaak`);
 * - daarna één stille herlading van de lijst en de menutellers, samengevoegd
 *   en op de achtergrond (`planStilleVerversing`): het scherm blijft staan en
 *   de server heeft het laatste woord.
 *
 * Alles hier gebeurt pas na een geslaagd antwoord van de server.
 *
 * Recensie blok 1: een herlading die al liep (de stille van een vorige
 * handeling, of een live-event) zou met oude data de optimistische update
 * overschrijven. Daarom eerst `cancelQueries` op wat we bijwerken, dan
 * `setQueryData`, en daarna opnieuw een stille herlading plannen.
 */
import type { QueryClient, QueryKey } from "@tanstack/react-query";

import type { Kanaal, V6Kaart, V6Lijst, V6Zaak } from "./api";
import type { LiveEvent } from "./live";
import { v6Sleutels } from "./sleutels";

/** Waar een kaart heen gaat; `null` = uit beeld (verwijderd). */
/** `verstuurd`: er ging net een bericht uit, dus dat is het laatste bericht (sessie 75). */
export type Bestemming =
  | { map: "afgerond" | "spam"; woord: string; verstuurd?: boolean }
  | { map: null };

export const AFGEROND_DOOR_JOU = "Afgerond door jou";
export const WACHT_OP_KLANT = "Wacht op klant";

/** De stille herlading wacht even, zodat een reeks handelingen er één geeft. */
export const STIL_NA_MS = 1_000;

function lijsten(qc: QueryClient) {
  return qc.getQueriesData<V6Lijst>({ queryKey: v6Sleutels.lijst });
}

/** Het filter uit de sleutel `["v6","lijst", map, kanaal, zoek]`. */
function filterVan(sleutel: QueryKey): { kanaal: Kanaal | null; zoek: string } {
  return {
    kanaal: (sleutel[3] as Kanaal | null | undefined) ?? null,
    zoek: (sleutel[4] as string | undefined) ?? "",
  };
}

/** De kaart van deze zaak uit de lijstcache (Open eerst), of null. */
export function kaartUitCache(qc: QueryClient, zaakId: string): V6Kaart | null {
  let elders: V6Kaart | null = null;
  for (const [, data] of lijsten(qc)) {
    const kaart = data?.kaarten.find((k) => k.zaak_id === zaakId);
    if (!kaart) continue;
    if (data?.map === "open") return kaart;
    elders ??= kaart;
  }
  return elders;
}

/** Zoek de kaart bij een live-event: op zaak, anders op gesprek. */
export function kaartBijEvent(qc: QueryClient, event: LiveEvent): V6Kaart | null {
  for (const [, data] of lijsten(qc)) {
    const kaart = data?.kaarten.find(
      (k) =>
        (event.zaak_id !== null && k.zaak_id === event.zaak_id) ||
        k.gesprek_ids.includes(event.gesprek_id),
    );
    if (kaart) return kaart;
  }
  return null;
}

/**
 * Sessie 54c (bevinding F): zaken die sinds de laatste stille herlading al
 * één keer van de tellers zijn afgetrokken. Zonder dit gaf "Chat 0" naast
 * een zichtbare chatkaart. Een tweede verplaatsing van dezelfde zaak (de eigen
 * handeling plus het live-event, of twee knoppen) telt niet nog eens.
 */
const afgetrokken = new WeakMap<QueryClient, Set<string>>();

/**
 * Haal de kaart uit Open en zet hem in `naar`, in elke lijst in de cache.
 * Tellers (open per kanaal, en het totaal) gaan één omlaag als de kaart
 * open stond. `kanaal` is de terugval als de kaart niet in de cache staat
 * (lijst met een zoekterm, of nog niet geladen).
 */
export async function verplaatsKaart(
  qc: QueryClient,
  zaakId: string,
  naar: Bestemming,
  kanaal: Kanaal | null = null,
): Promise<void> {
  await Promise.all([
    qc.cancelQueries({ queryKey: v6Sleutels.lijst }),
    qc.cancelQueries({ queryKey: v6Sleutels.menutellers }),
  ]);
  const kaart = kaartUitCache(qc, zaakId);
  const alle = lijsten(qc);
  const al = afgetrokken.get(qc) ?? new Set<string>();
  afgetrokken.set(qc, al);
  const wasOpen =
    !al.has(zaakId) &&
    (alle.some(([, d]) => d?.map === "open" && d.kaarten.some((k) => k.zaak_id === zaakId)) ||
      (!kaart && kanaal !== null));
  if (wasOpen) al.add(zaakId);
  const k = kaart?.kanaal ?? kanaal;
  const nu = new Date().toISOString();
  for (const [sleutel, data] of alle) {
    if (!data) continue;
    const filter = filterVan(sleutel);
    let kaarten = data.kaarten.filter((x) => x.zaak_id !== zaakId);
    if (
      kaart &&
      naar.map !== null &&
      data.map === naar.map &&
      !filter.zoek &&
      (filter.kanaal === null || filter.kanaal === kaart.kanaal)
    ) {
      kaarten = [
        {
          ...kaart,
          groep: naar.map,
          status_woord: naar.woord,
          toelichting: null,
          wacht_sinds: null,
          nieuw: false,
          stand: null,
          vinkbaar: false,
          afgerond_op: nu,
          moment: naar.verstuurd ? nu : kaart.moment,
        },
        ...kaarten,
      ];
      // Sessie 75: elke map staat op het laatste bericht; de kaart komt op
      // zijn eigen tijd, niet bovenaan.
      kaarten.sort((a, b) => Date.parse(b.moment) - Date.parse(a.moment));
    }
    const tellers = { ...data.tellers };
    let open = data.open;
    if (wasOpen && k) {
      tellers[k] = Math.max(0, (tellers[k] ?? 0) - 1);
      open = Math.max(0, open - 1);
    }
    qc.setQueryData<V6Lijst>(sleutel, { ...data, kaarten, tellers, open });
  }
  // De afgebroken herlading komt zo terug, met de stand van de server.
  planStilleVerversing(qc);
}

/**
 * De tegel na een stand-wissel uit het midden (sessie D, keuze 2 van 75): een
 * patch met `groep: "wacht"` betekent dat de zaak niet meer voor JP is. Verstuurd
 * (Wacht op klant) telt als Afgerond, gepland en doorgezet als Wacht. Zo is Voor
 * jou meteen schoon, voor de server de lijst opnieuw geeft (die zegt hetzelfde).
 */
export function tegelNaPatch(patch: Partial<V6Kaart>): Partial<V6Kaart> {
  if (patch.tegel !== undefined || patch.groep !== "wacht") return patch;
  return { ...patch, tegel: patch.status_woord === WACHT_OP_KLANT ? "afgerond" : "wacht" };
}

/** Werk een kaart bij uit een live-event (moment, nieuw). Geen herlading. */
export function werkKaartBij(qc: QueryClient, zaakId: string, patch: Partial<V6Kaart>): void {
  patch = tegelNaPatch(patch);
  for (const [sleutel, data] of lijsten(qc)) {
    if (!data || !data.kaarten.some((k) => k.zaak_id === zaakId)) continue;
    qc.setQueryData<V6Lijst>(sleutel, {
      ...data,
      kaarten: data.kaarten.map((k) => (k.zaak_id === zaakId ? { ...k, ...patch } : k)),
    });
  }
}

/** Nieuwe stand van de zaak in de cache (bijvoorbeeld afgerond). */
export async function zetZaak(
  qc: QueryClient,
  zaakId: string,
  patch: Partial<V6Zaak>,
): Promise<void> {
  await qc.cancelQueries({ queryKey: v6Sleutels.zaak(zaakId), exact: true });
  qc.setQueryData<V6Zaak>(v6Sleutels.zaak(zaakId), (oud) => (oud ? { ...oud, ...patch } : oud));
}

/** Eén zaak op de achtergrond opnieuw ophalen; het scherm blijft staan. */
export function verversZaak(qc: QueryClient, zaakId: string): void {
  void qc.invalidateQueries({ queryKey: v6Sleutels.zaak(zaakId), exact: true });
}

const gepland = new WeakMap<QueryClient, ReturnType<typeof setTimeout>>();

/**
 * Eén stille herlading van de lijst en de menutellers, na `STIL_NA_MS`.
 * Meer handelingen binnen die tijd geven samen één herlading.
 */
export function planStilleVerversing(qc: QueryClient, na: number = STIL_NA_MS): void {
  if (gepland.has(qc)) return;
  gepland.set(
    qc,
    setTimeout(() => {
      gepland.delete(qc);
      // De server telt opnieuw; vanaf nu mag elke zaak weer één keer af.
      afgetrokken.delete(qc);
      void qc.invalidateQueries({ queryKey: v6Sleutels.lijst });
      void qc.invalidateQueries({ queryKey: v6Sleutels.menutellers });
    }, na),
  );
}

/** Alleen voor tests: een geplande herlading vergeten. */
export function vergeetGeplandeVerversing(qc: QueryClient): void {
  const t = gepland.get(qc);
  if (t) clearTimeout(t);
  gepland.delete(qc);
}

export type Afronding = "sluiten" | "spam" | "verwijderen";

/** De afronding bij de reden van de server ("spam", "verwijderd", de rest is sluiten). */
export function afrondingVanReden(reden: string | null | undefined): Afronding {
  if (reden === "spam") return "spam";
  if (reden === "verwijderd") return "verwijderen";
  return "sluiten";
}

const BESTEMMING: Record<Afronding, Bestemming> = {
  sluiten: { map: "afgerond", woord: AFGEROND_DOOR_JOU },
  spam: { map: "spam", woord: "Spam" },
  verwijderen: { map: null },
};

/**
 * Na Sluiten, Spam, Verwijderen of een afrondvoorstel: kaart naar zijn map,
 * zaak op afgerond, één zaak en de lijst stil opnieuw.
 */
export async function naAfronden(
  qc: QueryClient,
  zaakId: string,
  soort: Afronding,
  kanaal: Kanaal | null = null,
): Promise<void> {
  const naar = BESTEMMING[soort];
  await verplaatsKaart(qc, zaakId, naar, kanaal);
  await zetZaak(qc, zaakId, {
    stand: "afgerond",
    groep: naar.map === "spam" ? "spam" : "afgerond",
    status_woord: naar.map === null ? "Verwijderd" : naar.woord,
    wacht_sinds: null,
  });
  verversZaak(qc, zaakId);
  planStilleVerversing(qc);
}

/** Na Ongedaan maken of iets anders dat de zaak terugzet: zaak en lijst stil opnieuw. */
export function naTerugzetten(qc: QueryClient, zaakId: string): void {
  verversZaak(qc, zaakId);
  planStilleVerversing(qc, 0);
}
